// Simple line diff for knowledge-base revisions. `components/diff-viewer.tsx`
// is a run-bound placeholder (it fetches a run's files), so it doesn't fit a
// text-vs-text comparison; this is a small LCS line diff with collapsed
// unchanged stretches instead. Pages are ≤ 64 KB, so O(n·m) after trimming
// the common prefix/suffix is fine.

import { useMemo, useState } from 'react'

export type DiffLine =
  | { kind: 'same'; text: string; a: number; b: number }
  | { kind: 'add'; text: string; b: number }
  | { kind: 'del'; text: string; a: number }

/** Above this many LCS cells (16 MB of Uint32) the middle is shown as a plain replace. */
const MAX_CELLS = 4_000_000

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n')
  const b = after.split('\n')
  let start = 0
  while (start < a.length && start < b.length && a[start] === b[start]) start++
  let endA = a.length
  let endB = b.length
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--
    endB--
  }

  const out: DiffLine[] = []
  for (let i = 0; i < start; i++)
    out.push({ kind: 'same', text: a[i]!, a: i + 1, b: i + 1 })

  const n = endA - start
  const m = endB - start
  if (n * m > MAX_CELLS) {
    for (let i = start; i < endA; i++)
      out.push({ kind: 'del', text: a[i]!, a: i + 1 })
    for (let j = start; j < endB; j++)
      out.push({ kind: 'add', text: b[j]!, b: j + 1 })
  } else {
    // lcs[i][j] = LCS length of a[start+i..endA) and b[start+j..endB)
    const width = m + 1
    const lcs = new Uint32Array((n + 1) * width)
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i * width + j] =
          a[start + i] === b[start + j]
            ? lcs[(i + 1) * width + j + 1]! + 1
            : Math.max(lcs[(i + 1) * width + j]!, lcs[i * width + j + 1]!)
      }
    }
    let i = 0
    let j = 0
    while (i < n || j < m) {
      if (i < n && j < m && a[start + i] === b[start + j]) {
        out.push({ kind: 'same', text: a[start + i]!, a: start + i + 1, b: start + j + 1 })
        i++
        j++
      } else if (
        j < m &&
        (i >= n || lcs[i * width + j + 1]! >= lcs[(i + 1) * width + j]!)
      ) {
        out.push({ kind: 'add', text: b[start + j]!, b: start + j + 1 })
        j++
      } else {
        out.push({ kind: 'del', text: a[start + i]!, a: start + i + 1 })
        i++
      }
    }
  }

  for (let i = endA, j = endB; i < a.length; i++, j++)
    out.push({ kind: 'same', text: a[i]!, a: i + 1, b: j + 1 })
  return out
}

type Row = DiffLine | { kind: 'gap'; count: number; from: number }

const CONTEXT = 3

function collapse(lines: DiffLine[]): Row[] {
  const keep = new Array<boolean>(lines.length).fill(false)
  lines.forEach((line, index) => {
    if (line.kind === 'same') return
    for (
      let k = Math.max(0, index - CONTEXT);
      k <= Math.min(lines.length - 1, index + CONTEXT);
      k++
    )
      keep[k] = true
  })
  const rows: Row[] = []
  let index = 0
  while (index < lines.length) {
    if (keep[index]) {
      rows.push(lines[index]!)
      index++
      continue
    }
    const from = index
    while (index < lines.length && !keep[index]) index++
    rows.push({ kind: 'gap', count: index - from, from })
  }
  return rows
}

/** Unified line diff between two texts, unchanged stretches collapsed. */
export function KbDiff({
  before,
  after,
  beforeLabel,
  afterLabel,
}: {
  before: string
  after: string
  beforeLabel?: string
  afterLabel?: string
}) {
  const lines = useMemo(() => diffLines(before, after), [before, after])
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set())
  const rows = useMemo(() => collapse(lines), [lines])
  const added = lines.filter((line) => line.kind === 'add').length
  const removed = lines.filter((line) => line.kind === 'del').length

  return (
    <div className="panel overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-sunken px-4 py-2 text-sm">
        <span className="text-ink-2">
          {beforeLabel ?? 'Before'} <span className="text-ink-3">→</span>{' '}
          {afterLabel ?? 'After'}
        </span>
        <span>
          <span className="text-ok">+{added}</span>{' '}
          <span className="text-danger">-{removed}</span>
        </span>
      </header>
      {added === 0 && removed === 0 ? (
        <p className="px-4 py-4 text-base text-ink-3">No differences.</p>
      ) : (
        <div className="overflow-x-auto">
          <pre className="min-w-full py-1 font-mono text-xs leading-relaxed">
            {rows.map((row, index) => {
              if (row.kind === 'gap') {
                if (expanded.has(row.from)) {
                  return lines
                    .slice(row.from, row.from + row.count)
                    .map((line, k) => <Line key={`${index}-${k}`} line={line} />)
                }
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() =>
                      setExpanded((current) => new Set(current).add(row.from))
                    }
                    className="block w-full bg-sunken/60 px-4 py-0.5 text-left text-ink-3 hover:text-ink"
                  >
                    ⋯ {row.count} unchanged line{row.count === 1 ? '' : 's'}
                  </button>
                )
              }
              return <Line key={index} line={row} />
            })}
          </pre>
        </div>
      )}
    </div>
  )
}

function Line({ line }: { line: DiffLine }) {
  const tone =
    line.kind === 'add'
      ? 'bg-ok-soft text-ok'
      : line.kind === 'del'
        ? 'bg-danger-soft text-danger'
        : 'text-ink-2'
  const sign = line.kind === 'add' ? '+' : line.kind === 'del' ? '-' : ' '
  return (
    <div className={`flex ${tone}`}>
      <span className="w-10 shrink-0 select-none pr-2 text-right text-ink-3">
        {line.kind === 'add' ? '' : line.a}
      </span>
      <span className="w-10 shrink-0 select-none pr-2 text-right text-ink-3">
        {line.kind === 'del' ? '' : line.b}
      </span>
      <span className="select-none pr-2">{sign}</span>
      <span className="whitespace-pre-wrap break-all pr-4">{line.text || ' '}</span>
    </div>
  )
}
